import { httpApi } from '@api/http.api.ts';
import { CONTENT_TYPE_FORM_DATA_HEADER, NO_REQUEST_TIMEOUT } from '@constants/api';
import type { EmploymentDetails, UserPersonalData } from '@routes/___shared___/user.api.ts';
import type { LoginResponse } from '@routes/auth/___shared___/auth.api.ts';
import type { EmptyResultsDescription, PaginatedResponse, UserRequestChangesStatus } from '@types';
import { getPercentage } from '@utils/numbers.ts';

export interface PasswordRequest {
    oldPassword: string;
    newPassword: string;
}

export interface UserProfileChange {
    id: number;
    userId: number;
    fieldName: string;
    oldValue: string;
    newValue: string;
    status: UserRequestChangesStatus;
    createdAt: string;
    decidedAt: string;
    decidedBy: number;
    comment: string;
    additionalDetails: string | null;
}

export interface AvatarUploadResponse {
    contentId: string;
    avatarURI: string;
}

export type UpdateUserDetailsParametersRequest = UserPersonalData &
    EmploymentDetails & {
        [key: string]: string;
    };

export const getUserRequestChanges = (): Promise<PaginatedResponse<UserProfileChange, EmptyResultsDescription>> =>
    httpApi
        .post<PaginatedResponse<UserProfileChange, EmptyResultsDescription>>(`me/parameters-change-requests`, {})
        .then(({ data }) => data);

export const updateUserDetailsParams = (UpdateUserConsentPayload: UpdateUserDetailsParametersRequest): Promise<any> =>
    httpApi
        .post<any>(`me/parameters-change-request`, {
            ...UpdateUserConsentPayload,
        })
        .then(({ data }) => data);

export const updateProfilePhotoWithUploadProgress = (
    formData: FormData,
    onUploadProgress?: (progress: number) => void,
    signal?: AbortSignal,
): Promise<AvatarUploadResponse> =>
    httpApi
        .post<AvatarUploadResponse>('imageupload/avatar', formData, {
            headers: CONTENT_TYPE_FORM_DATA_HEADER,
            signal,
            timeout: NO_REQUEST_TIMEOUT,
            onUploadProgress: (event) => {
                if (!event.total) return;
                onUploadProgress?.(getPercentage(event.loaded, event.total));
            },
        })
        .then(({ data }) => data);

export const deleteUploadedPhoto = (): Promise<any> =>
    httpApi.delete<any>(`imageupload/purge-candidates`).then(({ data }) => data);

export const changePassword = (changePasswordPayload: PasswordRequest): Promise<LoginResponse> =>
    httpApi.put<LoginResponse>(`auth/password`, { ...changePasswordPayload }).then(({ data }) => data);
